/* eslint-disable max-statements */
import { Grid, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useMemo } from 'react';

import { Batch, Customer, Declarant, Declaration, EnvironmentCountries, LowValuesDeclarantDataSource } from '@e-origin/shared';

import { EucdmCode, HideElementFor, TextInput } from '@components';
import { REPRESENTATIVE_STATUS_DECLARANT, REPRESENTATIVE_STATUS_DIRECT } from '@constants';
import { useCustomer } from '@hooks/customer';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { DeclarationStakeholdersFormValues } from './validateDeclarationStakeholdersForm';

type ExtendedDeclaration = Declaration & {
  customer?: Customer;
};

interface DeclarantAndRepresentativeFormProps {
  declaration: ExtendedDeclaration;
  form: UseFormReturnType<DeclarationStakeholdersFormValues>;
  declarant?: Declarant;
  declarationGroup: string;
}

const getDeclarantIdentificationNumberIDMS = (declaration: ExtendedDeclaration, customer: Customer, isExport: boolean) => {
  const isHighValue = isHighValueDeclaration(declaration.generalInfo.group);
  const declarationCustomer = isHighValue ? declaration.customer : customer;

  if (declaration.customsReport?.declarant?.identificationNumber) {
    return declaration.customsReport?.declarant?.identificationNumber;
  }

  if (
    customer.declarant?.useImporterEORI &&
    (declarationCustomer.representative.status === REPRESENTATIVE_STATUS_DIRECT ||
      (declarationCustomer.representative.status === REPRESENTATIVE_STATUS_DECLARANT && isHighValue))
  ) {
    return isExport ? declaration.stakeholders?.exporter?.identificationNumber : declaration.stakeholders?.importer?.identificationNumber;
  }

  return declarationCustomer.declarant.identificationNumberIDMS;
};

export const DeclarantAndRepresentativeForm: React.FC<DeclarantAndRepresentativeFormProps> = ({ declaration, form, declarationGroup }) => {
  const { data: settings } = useSettings();
  const { data: declarant } = useDeclarant();
  const { data: customer } = useCustomer((declaration.messageInfo?.batch as unknown as Batch).customer);

  const isHighValue = isHighValueDeclaration(declaration?.generalInfo?.group);
  const isNetherlands = settings?.country === EnvironmentCountries.NL;
  const isBelgium = settings?.country === EnvironmentCountries.BE;
  const isExport = declaration?.generalInfo?.isExport;

  const isHighValueBE = isHighValue && isBelgium;

  const useCustomerData =
    !isHighValue && (declarant as unknown as Declarant)?.functionalFeatures?.lowValuesDeclarantDataSource === LowValuesDeclarantDataSource.CUSTOMER;

  const declarationDeclarant = useMemo((): any => {
    if (declaration?.customsReport?.declarant) {
      return declaration?.customsReport?.declarant;
    }

    if (useCustomerData) {
      return customer?.declarant;
    }

    if (customer?.declarant?.useImporterEORI && declaration?.customer?.representative?.status === REPRESENTATIVE_STATUS_DECLARANT && isHighValue) {
      return isExport ? declaration?.stakeholders?.exporter : declaration?.stakeholders?.importer;
    }

    return declaration?.customer?.declarant;
  }, [settings?.country, declaration, customer, useCustomerData]);

  const representative = useMemo((): any => {
    if (declaration?.customsReport?.declarant?.representative) {
      return declaration?.customsReport?.declarant?.representative;
    }

    if (useCustomerData) {
      return customer?.representative;
    }

    return declaration?.customer?.representative;
  }, [settings?.country, declaration, customer, useCustomerData]);

  if (!settings || !declarationDeclarant || !customer) {
    return null;
  }

  return (
    <>
      <Stack mb="xl">
        <Title order={5} fw={600}>
          Declarant
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.declarant.name">
              <TextInput {...form.getInputProps('stakeholders.declarant.name')} label="Name" value={declarationDeclarant.name} disabled />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            {!(isBelgium && isExport) && (
              <EucdmCode path="stakeholders.declarant.identificationNumber">
                <TextInput
                  label={`Identification number ${isNetherlands ? 'DMS' : 'IDMS'}`}
                  value={getDeclarantIdentificationNumberIDMS(declaration, customer, isExport) ?? ''}
                  disabled
                />
              </EucdmCode>
            )}
            {isBelgium && isExport && (
              <TextInput
                {...form.getInputProps('stakeholders.declarant.identificationNumber')}
                label="Identification number"
                value={declarationDeclarant.identificationNumber}
                disabled
              />
            )}
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.declarant.address.street">
              <TextInput
                {...form.getInputProps('stakeholders.declarant.address.street')}
                label="Street and number"
                value={declarationDeclarant.address?.street}
                disabled
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.declarant.address.postcode">
              <TextInput
                {...form.getInputProps('stakeholders.declarant.address.postcode')}
                label="Postcode"
                value={declarationDeclarant.address?.postcode}
                disabled
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.declarant.address.city">
              <TextInput {...form.getInputProps('stakeholders.declarant.address.city')} label="City" value={declarationDeclarant.address?.city} disabled />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.declarant.address.country">
              <TextInput label="Country" value={declarationDeclarant.address?.country ?? ''} disabled />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        {isHighValueBE && !isExport && (
          <Grid>
            <Grid.Col span={3}>
              <TextInput
                {...form.getInputProps('stakeholders.declarant.identificationNumber')}
                label="Identification number"
                value={declarationDeclarant.identificationNumber}
                disabled
              />
            </Grid.Col>
          </Grid>
        )}

        <Stack mt="md">
          <Title order={5} fw={600}>
            Declarant Contact Person
          </Title>

          <Grid>
            <Grid.Col span={4}>
              <EucdmCode path="stakeholders.declarant.contactPerson.name">
                <TextInput
                  {...form.getInputProps('stakeholders.declarant.contactPerson.name')}
                  label="Name"
                  value={declarationDeclarant.contactPerson?.name}
                  disabled
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path="stakeholders.declarant.contactPerson.phoneNumber">
                <TextInput
                  {...form.getInputProps('stakeholders.declarant.contactPerson.phoneNumber')}
                  label="Phone number"
                  value={declarationDeclarant.contactPerson?.phoneNumber}
                  disabled
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path="stakeholders.declarant.contactPerson.email">
                <TextInput
                  {...form.getInputProps('stakeholders.declarant.contactPerson.email')}
                  label="Email"
                  value={declarationDeclarant.contactPerson?.email}
                  disabled
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>
      </Stack>

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Representative
        </Title>

        <Grid>
          <HideElementFor systems={['DMS', 'DECO']} declarationGroup={declarationGroup}>
            <Grid.Col span={6}>
              <TextInput disabled label="Name" value={representative?.name ?? ''} />
            </Grid.Col>
          </HideElementFor>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.representative.identificationNumber">
              <TextInput
                disabled
                name="declaration.customer.representative.identificationNumberIDMS"
                label={`Identification number ${isNetherlands ? 'DMS' : 'IDMS'}`}
                value={
                  declaration?.customsReport?.declarant?.representative?.identificationNumber ??
                  declaration?.customer?.representative?.identificationNumberIDMS ??
                  customer?.representative?.identificationNumberIDMS ??
                  ''
                }
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <HideElementFor systems={['DMS', 'DECO']} declarationGroup={declarationGroup}>
          <Grid>
            <Grid.Col span={3}>
              <EucdmCode path="stakeholders.representative.address.street">
                <TextInput disabled label="Street and number" value={representative?.address?.street ?? ''} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={3}>
              <EucdmCode path="stakeholders.representative.address.postcode">
                <TextInput disabled label="Postcode" value={representative?.address?.postcode ?? ''} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={3}>
              <EucdmCode path="stakeholders.representative.address.city">
                <TextInput disabled label="City" value={representative?.address?.city ?? ''} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={3}>
              <EucdmCode path="stakeholders.representative.address.country">
                <TextInput disabled label="Country" value={representative?.address?.country ?? ''} />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </HideElementFor>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.representative.status">
              <TextInput disabled label="Status" value={representative?.status ?? ''} />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
};
